import { supabase } from "@/integrations/supabase/client";
import type { ResourceDocument } from "@/types/agent/info-links";

export interface DescribeResourceItem {
  id: string;
  kind: "link" | "document";
  label: string;
  url: string;
  fileName?: string | null;
}

export interface DescribeResourceResult {
  id: string;
  kind: "link" | "document";
  description: string | null;
  suggestedLabel: string | null;
  basis: "content" | "name" | "failed";
  /** Links only: whether the URL answered. Null when it wasn't checked. */
  reachable: boolean | null;
}

export interface LinkCheckResult {
  id: string;
  reachable: boolean | null;
}

export interface KbResourceCandidate {
  /** The document id, or `documentId/fileName` for one picture of a batch that holds several. */
  key: string;
  documentId: string;
  name: string;
  fileName: string;
  extension: string;
  summary: string | null;
}

export interface KbResourceImportResult {
  documents: ResourceDocument[];
  failed: Array<{ documentId: string; name: string | null; reason: string }>;
}

/** Must match MAX_DESCRIBE_ITEMS in handle-agent/resources.ts. */
export const DESCRIBE_BATCH_SIZE = 6;

/** Must match MAX_CHECK_LINKS in handle-agent/resources.ts. */
const CHECK_LINKS_BATCH_SIZE = 25;

const invoke = async <T>(action: string, body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke("handle-agent", { body: { action, ...body } });
  if (error) throw error;
  if (data?.error) throw new Error(typeof data.error === "string" ? data.error : "Request failed");
  return data as T;
};

/** Live-page resources (links & downloads): AI descriptions and knowledge-base reuse. */
export class AgentResourcesService {
  static async describe(agentId: string, items: DescribeResourceItem[]): Promise<DescribeResourceResult[]> {
    const data = await invoke<{ results: DescribeResourceResult[] }>("describe-resources", { agentId, items });
    return data.results ?? [];
  }

  static async checkLinks(agentId: string, links: Array<{ id: string; url: string }>): Promise<LinkCheckResult[]> {
    const batches: Array<Array<{ id: string; url: string }>> = [];
    for (let i = 0; i < links.length; i += CHECK_LINKS_BATCH_SIZE) batches.push(links.slice(i, i + CHECK_LINKS_BATCH_SIZE));
    const responses = await Promise.all(
      batches.map((batch) =>
        invoke<{ results: LinkCheckResult[] }>("check-resource-links", { agentId, links: batch }),
      ),
    );
    return responses.flatMap((response) => response.results ?? []);
  }

  static async listKbCandidates(agentId: string): Promise<KbResourceCandidate[]> {
    const data = await invoke<{ candidates: KbResourceCandidate[] }>("list-kb-resources", { agentId });
    return data.candidates ?? [];
  }

  static async importFromKb(agentId: string, keys: string[]): Promise<KbResourceImportResult> {
    const data = await invoke<KbResourceImportResult>("import-kb-resources", { agentId, documentIds: keys });
    return { documents: data.documents ?? [], failed: data.failed ?? [] };
  }
}
